import { View } from 'react-native';

import { useLoyaltyPoints } from 'hooks/loyalty/use-loyalty-points';
import { useFilteredRewardCategories } from 'pages/loyalty/loyalty-rewards/use-filtered-reward-categories';
import { LoyaltyUser } from 'state/global-state/models/loyalty/user/user.types';
import { IEngineRewardsMap } from 'state/loyalty/types';

import { BottomLinks } from './components/bottom-links';
import { CrownIcon } from './components/crown-icon';
import { ExpirationWarning } from './components/expiration-warning';
import { useLastSeenLoyaltyPoints } from './components/hooks/use-last-seen-loyalty-points';
import { useProgressCalculation } from './components/hooks/use-progress-calculation';
import { useTiers } from './components/hooks/use-tiers';
import { ProgressSection } from './components/progress-section';
import { RedeemCard } from './components/redeem-card';
import { ProfileCardSkeleton } from './profile-card-skeleton';
import { styles } from './profile-card.styles';

interface ProfileCardProps {
  loyaltyUser: LoyaltyUser;
  engineRewardsMap: IEngineRewardsMap;
  loading: boolean;
}

export const ProfileCard = ({ loyaltyUser, engineRewardsMap, loading }: ProfileCardProps) => {
  const { name } = loyaltyUser || {};
  const userName = name?.split(' ')[0];

  const rewardCategories = useFilteredRewardCategories();

  const { tiers } = useTiers({ engineRewardsMap, rewardCategories });
  const {
    userHasExpiringPoints,
    pointsExpirationDate,
    currentPoints,
    pointsToExpire,
  } = useLoyaltyPoints(loyaltyUser);

  // Loads the last seen loyalty points from the local storage
  const { lastSeenLoyaltyPoints } = useLastSeenLoyaltyPoints(currentPoints);

  const { progressData, animationState } = useProgressCalculation({
    currentPoints,
    lastSeenLoyaltyPoints,
    tiers,
  });

  const isCardLoading = loading || !loyaltyUser?.id || !rewardCategories?.length;

  if (isCardLoading) {
    return <ProfileCardSkeleton />;
  }

  return (
    <>
      <View style={styles.mainCard}>
        <CrownIcon />
        <View style={styles.progressContainer}>
          <ProgressSection
            userName={userName}
            tiers={tiers}
            currentPoints={currentPoints}
            lastSeenLoyaltyPoints={lastSeenLoyaltyPoints}
            progressData={progressData}
            animationState={animationState}
          />
        </View>

        {userHasExpiringPoints && (
          <ExpirationWarning
            pointsToExpire={pointsToExpire}
            expirationDate={pointsExpirationDate.raw}
          />
        )}
      </View>

      {/* Visible only if the user has reached the first tier */}
      {progressData.currentTier > 0 && <RedeemCard />}

      <BottomLinks loyaltyUserId={loyaltyUser.id} loading={isCardLoading} />
    </>
  );
};
